/**
 * Greek typography the browser does not do for us.
 *
 * Capitals: Greek written in capitals carries no accents — ΠΑΡΑΓΓΕΛΙΕΣ, never ΠΑΡΑΓΓΕΛΊΕΣ. CSS
 * `text-transform: uppercase` gets this right in Chrome but NOT in Safari (WebKit only special-cases
 * Turkish), and HQ lives on iPhones, so labels printed in capitals are upper-cased here instead.
 * An accented vowel followed by ι or υ gets a diaeresis on the second letter, so that the two
 * letters are still read apart: Μάιος → ΜΑΪΟΣ, τσάι → ΤΣΑΪ. The word «ή» ("or") keeps its accent.
 */

const ACCENT = /[̀́͂̓̔ͅ]/
const MARK = /\p{M}/u
const GREEK = /[Ͱ-Ͽἀ-῿]/
const DISJUNCTIVE_ETA = ""

export function caps(text: string): string {
  // «ή» on its own keeps its accent in capitals (Ή), so it is set aside first.
  const marked = text.replace(/(^|[^\p{L}])ή(?=[^\p{L}]|$)/gu, `$1${DISJUNCTIVE_ETA}`)
  const chars = Array.from(marked.normalize("NFD"))
  let out = ""
  let previous = ""
  let previousAccented = false
  for (let i = 0; i < chars.length; i++) {
    const char = chars[i]
    if (char === DISJUNCTIVE_ETA) {
      out += "Ή"
      previous = ""
      previousAccented = false
      continue
    }
    let end = i + 1
    const marks: string[] = []
    while (end < chars.length && MARK.test(chars[end])) marks.push(chars[end++])
    i = end - 1
    if (MARK.test(char)) continue

    const lower = char.toLowerCase()
    if (!GREEK.test(char)) {
      out += char.toUpperCase() + marks.join("")
      previous = ""
      previousAccented = false
      continue
    }
    const accented = marks.some((mark) => ACCENT.test(mark))
    const kept = marks.filter((mark) => !ACCENT.test(mark))
    // ά+ι, έ+ι, ό+ι, ύ+ι and ά+υ, έ+υ, ό+υ, ή+υ would read as one sound in capitals without accents.
    const apart = previousAccented && ((lower === "ι" && "αεου".includes(previous)) || (lower === "υ" && "αεοη".includes(previous)))
    if (apart && !kept.includes("̈")) kept.push("̈")
    out += (lower === "ς" ? "Σ" : char.toUpperCase()) + kept.join("")
    previous = lower
    previousAccented = accented
  }
  return out.normalize("NFC")
}

/** Month names on their own, as a calendar heads a month (nominative: Σεπτέμβριος, not Σεπτεμβρίου). */
export const MONTHS_EL = ["Ιανουάριος", "Φεβρουάριος", "Μάρτιος", "Απρίλιος", "Μάιος", "Ιούνιος", "Ιούλιος", "Αύγουστος", "Σεπτέμβριος", "Οκτώβριος", "Νοέμβριος", "Δεκέμβριος"] as const
export const MONTHS_EL_SHORT = ["Ιαν", "Φεβ", "Μάρ", "Απρ", "Μάι", "Ιούν", "Ιούλ", "Αύγ", "Σεπ", "Οκτ", "Νοέ", "Δεκ"] as const
